import {
    GetFolderResponse,
    TopicSearchResult
} from '../../types';
import { LitElement, css, html, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators';
import { getFolder, getFolders, getTopicsForFolder } from '../../helpers/api/folders-api';

import { detectMobileViewport } from '../../helpers/device';
import { getQueryStringParameters } from '../../helpers/urls';
import { getTopicsforBackpack } from '../../helpers/api/topics-api';
import { heights } from '../../style/variables';
import { isDefined } from '../../helpers/utils';

@customElement('folder-page')
export class FolderPage extends LitElement {
    static amplitude_callsite: string = 'Folder Page';

    @state()
    allFolders: GetFolderResponse[] = [];

    @state()
    folders: GetFolderResponse[] = [];

    @state()
    folderBreadcrumb?: any[];

    @state()
    folderId?: string;

    @state()
    folderInfo?: GetFolderResponse;

    @state()
    topics: TopicSearchResult[] = [];

    @state()
    originTopics: TopicSearchResult[] = [];

    @state()
    foldersTabSelected: boolean = false;

    @state()
    loading: boolean = false;

    // listen for changes to the url hash to update the folderId
    connectedCallback() {
        super.connectedCallback();
        window.onhashchange = () => {
            const queryString = getQueryStringParameters();
            this.folderId = queryString.get('folder_id');
        };
    }

    // listen for changes to the folderId
    async updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('folderId')) {
            await this.startup();
        }
    }


    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        this.loading = true;
        const queryString = getQueryStringParameters();
        this.folderId = queryString.get('folder_id');
        if (this.folderId === undefined) {
            this.foldersTabSelected = true;
        }
        await this.refreshFolder();
        await this.refreshChildren();
        this.loading = false;

        window.dispatchEvent(new CustomEvent('page-content-ready'));
    }

    private async goToEmptyFolder() {
        this.foldersTabSelected = true;
        this.folderInfo = undefined;
        this.folders = [];
        this.topics = [];
        window.location.assign('/#folder');
        await this.requestUpdate();
    }

    private async refreshFolder() {
        if (this.folderId !== undefined) {
            try {
                this.folderInfo = await getFolder(
                    FolderPage.amplitude_callsite,
                    this.folderId
                );

                // Load topics
                this.topics =
                    (await getTopicsForFolder(
                        FolderPage.amplitude_callsite,
                        this.folderId
                    )) ?? [];
                this.topics.sortBy((topic) => topic.updated_at);
            } catch (e) {
                window.location.assign('/#404');
            }
        } else if (detectMobileViewport()) {
            this.folderInfo = undefined;
            this.foldersTabSelected = true;
        } else {
            window.location.assign('/#404');
        }
        this.originTopics = await getTopicsforBackpack(FolderPage.amplitude_callsite);
    }

    private async refreshChildren() {
        this.allFolders = await getFolders(FolderPage.amplitude_callsite);
        if (this.folderInfo != null) {
            const folderChildrenIds = this.folderInfo.children.map((child) => child._id.$oid);
            const folderAncestorsIds = this.folderInfo.ancestors.map((ancestor) => ancestor.$oid);
            this.connectedCallback();
            const allAncestors = this.allFolders.filter((folder) =>
                folderAncestorsIds.includes(folder._id.$oid)
            );
            // Load sub folders and breadcrumb
            this.folders = await this.allFolders.filter((folder) =>
                folderChildrenIds.includes(folder._id.$oid)
            );
            this.folderBreadcrumb = allAncestors;
        }
    }

    private async folderClick(newFolderId: string) {
        if (newFolderId) {
            window.location.assign(`/#folder?folder_id=${newFolderId}`);
            if (this.folderId !== newFolderId) {
                this.folderId = newFolderId;
                await this.refreshFolder();
                await this.refreshChildren();
            }
        }
    }

    render() {
        if (this.loading) {
            return html`
                <div class="pageContainer">
                    <loading-element loadingString="Loading your folder elements..."></loading-element>
                </div>
            `;
        }

        return html`
            <div class="pageContainer">
                <div class="mainContent">
                    <div class="breadcrumbWrapper">
                        ${this.folderInfo !== undefined ? html`
                            <div class="breadcrumbFolders">
                                <mwc-icon id="breadcrumbFolderIcon">folder</mwc-icon>
                                ${this.folderBreadcrumb?.map(
                                    (folder, index) => html`
                                        <div
                                            class=${isDefined(folder._id) && 'breadcrumbItem'}
                                            @click=${async () =>
                                                await this.folderClick(folder._id?.$oid)}>
                                            ${folder.name}
                                        </div>
                                        <div class="breadcrumbSeparator">></div>
                                    `
                                )}
                            </div>
                            <div class="breadcrumbItem">
                                ${this.folderInfo?.name}
                            </div>
                        ` : ''}
                    </div>
                    <div class="backpackWrapper">
                        ${this.folders?.map(
                            (folder) => html`
                                <div class="column-item">
                                    <folder-card @click=${async () => await this.folderClick(folder._id.$oid)}>
                                        ${folder.name}
                                    </folder-card>
                                </div>`
                        )}
                    </div>
                    <div class="backpackWrapper">
                        ${this.topics.map((topic) => {
                            return html`
                                <div class="column-item">
                                    <backpack-card
                                        .topic=${topic}
                                        .selectedFolderId=${this.folderId}
                                        .showRemoveFromFolder=${true}
                                        @on-remove-topic-from-folder=${this.refreshFolder}>
                                    </backpack-card>
                                </div>`;
                        })}
                    </div>
                </div>
            </div>
            <div class="bottomTabs mobileOnly">
                <div
                    class="tabItem ${this.foldersTabSelected ? 'active' : ''}"
                    @click=${this.goToEmptyFolder}>
                    <mwc-icon>folder</mwc-icon>
                    <div>Folders</div>
                </div>
                <a class="tabItem" href="#home">
                    <mwc-icon>home</mwc-icon>
                    <div>Backpack</div>
                </a>
                <a class="tabItem" href="#view-creator-profiles">
                    <mwc-icon>account_circle</mwc-icon>
                    <div>Profiles</div>
                </a>
            </div>

            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            .pageContainer {
                display: flex;
                width: 100%;
                height: calc(100vh - ${unsafeCSS(heights.nav)});
                box-sizing: border-box;
                align-items: stretch;
            }

            .bottomTabs {
                position: fixed;
                bottom: 0;
                width: 100%;
                box-sizing: border-box;
                align-items: center;
                justify-content: space-around;
                background: var(--main-secondary-color);
                color: var(--theme-white-color);
                z-index: 2;
            }

            .bottomTabs .tabItem {
                display: flex;
                flex: 1;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                padding: 0.5rem;
                color: var(--theme-white-color);
                border-top: 4px solid transparent;
                cursor: pointer;
                text-decoration: none;
            }

            .bottomTabs .tabItem.active {
                color: var(--main-primary-color);
                border-top: 4px solid var(--main-primary-color);
            }

            .sidebar {
                border-right: 1px solid #ECECEC;
                box-sizing: border-box;
                height: 100vh;
                box-shadow: 1px 0 2px 0 rgba(0,0,40,.08),6px 0 10px 0 rgba(0,0,40,.08);
                background-color: white;
                border-radius: 0 8px 8px 0;
            }

            .mainContent {
                flex: 1;
                width: 100%;
                padding: 1.5rem;
                box-sizing: border-box;
                overflow: auto;
            }

            .breadcrumbWrapper {
                max-width: 100vw;
                display: flex;
                align-items: center;
                flex-wrap: nowrap;
                font-size: 24px;
                color: var(--main-primary-color);
            }

            .breadcrumbFolders {
                display: flex;
                align-items: center;
                overflow: hidden;
            }

            .breadcrumbItem {
                padding-left: 0.5rem;
                padding-right: 0.5rem;
                white-space: nowrap;
                text-decoration: underline;
                cursor: pointer;
            }

            #breadcrumbFolderIcon {
                font-size: 50px;
                margin-right: 0.5rem;
                color: var(--main-secondary-color);
            }

            .backpackWrapper {
                width: 100%;
                box-sizing: border-box;
                margin: 0px -10px;
                display: flex;
                flex-wrap: wrap;
                overflow-y: auto; // DO NOT REMOVE - this allows scrolling on mobile
            }

            .column-item {
                width: 25%;
                min-width: 150px;
                padding: 10px;
                box-sizing: border-box;
            }

            .createNewMap {
                padding: 1.2rem;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100%;
                box-sizing: border-box;
            }

            .createTitle {
                color: var(--theme-input-color);
                font-size: 30px;
                font-weight: 700;
                margin-top: 1rem;
                margin-bottom: 2rem;
                cursor: pointer;
            }

            .createTemplate {
                font-size: 1rem;
                font-weight: 700;
                color: var(--theme-input-color);
            }

            .imageHolder {
                width: 100%;
                max-height: 180px;
                display: flex;
                justify-content: center;
                align-items: center;
                overflow: hidden;
            }

            .cardImg {
                flex-shrink: 0;
                min-width: 100%;
                min-height: 100%;
            }

            .topicMenu {
                position: absolute;
                top: 1rem;
                right: 1rem;
            }

            .actionButtons {
                display: flex;
                margin-top: 2rem;
                justify-content: space-between;
            }

            @media (min-width: 551px) {
                .desktopOnly {
                    display: block;
                }

                .mobileOnly {
                    display: none;
                }
            }

            @media (max-width: 550px) {
                .desktopOnly {
                    display: none;
                }

                .mobileOnly {
                    display: block;
                }

                .bottomTabs.mobileOnly {
                    display: flex;
                }

                .pageContainer {
                    flex-direction: column;
                    padding: 20px;
                }

                .sidebar {
                    width: 100%;
                    height: auto;
                    border-right: none;
                    border-bottom: 1px solid #ECECEC;
                }

                .mainContent {
                    padding: 1rem 1rem 3rem;
                }
            }
        `;
    }
}
